import {
    Bold,
    Book,
    Compass,
    Briefcase,
    ScanText,
    FilePlus2,
    Edit3,
    Paintbrush,
    Layout,
    Eye,
    PlusSquare,
    SquareMenuIcon,
    HelpCircleIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
    HelpGuideCreatingResume,
    HelpGuideEditingResume,
    HelpGuideAssistant,
    HelpGuideStyling,
    HelpGuideLayout,
    HelpGuidePreview,
    HelpGuideAddRemove,
    HelpGuideContextMenus,
    HelpGuideHotkeys,
    HelpGuideContact,
    HelpGuideSmartScribe,
    HelpGuideJobs,
    HelpGuideAtsScan,
} from "./components";
import { Button } from "@/components/Button/Button";
import { SmartScribe } from "@/components/Icons/Icons";
import { Modal } from "@/components/Modal/Modal";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { getIsModalOpen } from "@/store/modal/selectors";
import { closeModal, openModal } from "@/store/modal/slice";
import {
    getIs1TemplateTenant,
    getIsAtsScanEnabled,
    getIsAiEnabled,
    getIsUserJobsEnabled,
} from "@/store/tenant/selectors";
import { ModalTypes } from "@/types/modal";
import { trackPostHogEvent } from "@/utils/tracking";

const getNavigationItems = (
    isAiEnabled: boolean,
    isUserJobsEnabled: boolean,
    isAtsScanEnabled: boolean,
) => {
    const items = [
        {
            id: "creating-resume",
            label: "Creating a New Resume",
            icon: <FilePlus2 size={20} />,
        },
        {
            id: "editing-resume",
            label: "Editing your Resume",
            icon: <Edit3 size={20} />,
        },
    ];

    if (isAiEnabled) {
        items.push({
            id: "smart-scribe",
            label: "Smart Scribe",
            icon: (
                <SmartScribe
                    className="h-5 w-5"
                    gradient
                />
            ),
        });
    }

    if (isUserJobsEnabled) {
        items.push({
            id: "jobs",
            label: "Jobs",
            icon: <Briefcase size={20} />,
        });
    }

    if (isAtsScanEnabled) {
        items.push({
            id: "ats-scan",
            label: "ATS Scanner",
            icon: <ScanText size={20} />,
        });
    }

    items.push(
        {
            id: "assistant",
            label: "Using the Assistant",
            icon: <Compass size={20} />,
        },
        {
            id: "styling",
            label: "Styling your Resume",
            icon: <Paintbrush size={20} />,
        },
        {
            id: "layout",
            label: "Rearranging Sections",
            icon: <Layout size={20} />,
        },
        {
            id: "preview",
            label: "Previewing your Resume",
            icon: <Eye size={20} />,
        },
        {
            id: "add-remove-section",
            label: "Add/Remove Sections",
            icon: <PlusSquare size={20} />,
        },
        {
            id: "context-menus",
            label: "Context Menus",
            icon: <SquareMenuIcon size={20} />,
        },
        { id: "hotkeys", label: "Hotkeys", icon: <Bold size={20} /> },
        {
            id: "contact",
            label: "Contact Us",
            icon: <HelpCircleIcon size={20} />,
        },
    );

    return items;
};

const HelpGuideModal = () => {
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.HELP_GUIDE));
    const dispatch = useAppDispatch();
    const isUserJobsEnabled = useAppSelector(getIsUserJobsEnabled);
    const isAtsScanEnabled = useAppSelector(getIsAtsScanEnabled);
    const isAiEnabled = useAppSelector(getIsAiEnabled);
    const navigationItems = useMemo(
        () =>
            getNavigationItems(
                isAiEnabled,
                isUserJobsEnabled,
                isAtsScanEnabled,
            ),
        [isAiEnabled, isUserJobsEnabled, isAtsScanEnabled],
    );
    const [activeNavItem, setActiveNavItem] = useState(navigationItems[0].id);

    const onClose = useCallback(() => {
        dispatch(closeModal(ModalTypes.HELP_GUIDE));
    }, [dispatch]);

    const startTour = useCallback(() => {
        onClose();
        dispatch(openModal({ modalType: ModalTypes.TOUR }));
    }, [dispatch, onClose]);

    const handleNavigationItemClick = useCallback(
        (itemId: string, itemLabel: string) => {
            setActiveNavItem(itemId);
            trackPostHogEvent("help_guide_navigation_clicked", {
                component: "HelpGuideModal",
                element_id: itemId,
                button_text: itemLabel,
                feature_name: "help_guide",
                action_taken: "navigation_item_clicked",
            });
        },
        [],
    );

    return (
        <Modal
            title=""
            open={isOpen}
            width="4xl"
            noHeader
            modalType={ModalTypes.HELP_GUIDE}
        >
            <div className="fixed bottom-0 top-0 flex w-64 flex-col overflow-y-scroll py-6">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white">
                    <nav className="flex h-full flex-1 flex-col">
                        <ul
                            role="list"
                            className="flex flex-1 flex-col justify-between gap-y-3 overflow-scroll"
                        >
                            <li className="pr-6">
                                <Button
                                    variant={"outline"}
                                    leftIcon={<Book size={20} />}
                                    color={"primary"}
                                    onClick={startTour}
                                    className="!h-8 w-full !px-2 text-left leading-4"
                                >
                                    Builder Tutorial
                                </Button>
                            </li>
                            <li>
                                <ul
                                    role="list"
                                    className="space-y-2 pr-6"
                                >
                                    {navigationItems.map(item => (
                                        <li key={item.label}>
                                            <Button
                                                leftIcon={item.icon}
                                                color={
                                                    activeNavItem === item.id
                                                        ? "softIndigo"
                                                        : "white"
                                                }
                                                onClick={() =>
                                                    handleNavigationItemClick(
                                                        item.id,
                                                        item.label,
                                                    )
                                                }
                                                className="!h-8 w-full !justify-start !px-2 text-left leading-4"
                                            >
                                                {item.label}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
            <main className="max-h-[700px] pl-64">
                <div className="pb-6 pl-6">
                    {activeNavItem === "creating-resume" && (
                        <HelpGuideCreatingResume onClose={onClose} />
                    )}
                    {activeNavItem === "editing-resume" && (
                        <HelpGuideEditingResume
                            onClose={onClose}
                            setActiveNavItem={setActiveNavItem}
                        />
                    )}
                    {activeNavItem === "smart-scribe" && (
                        <HelpGuideSmartScribe onClose={onClose} />
                    )}
                    {activeNavItem === "jobs" && (
                        <HelpGuideJobs onClose={onClose} />
                    )}
                    {activeNavItem === "ats-scan" && (
                        <HelpGuideAtsScan onClose={onClose} />
                    )}
                    {activeNavItem === "assistant" && (
                        <HelpGuideAssistant onClose={onClose} />
                    )}
                    {activeNavItem === "styling" && (
                        <HelpGuideStyling onClose={onClose} />
                    )}
                    {activeNavItem === "layout" && (
                        <HelpGuideLayout onClose={onClose} />
                    )}
                    {activeNavItem === "preview" && (
                        <HelpGuidePreview onClose={onClose} />
                    )}
                    {activeNavItem === "add-remove-section" && (
                        <HelpGuideAddRemove onClose={onClose} />
                    )}
                    {activeNavItem === "context-menus" && (
                        <HelpGuideContextMenus onClose={onClose} />
                    )}
                    {activeNavItem === "hotkeys" && (
                        <HelpGuideHotkeys onClose={onClose} />
                    )}
                    {activeNavItem === "contact" && (
                        <HelpGuideContact onClose={onClose} />
                    )}
                </div>
            </main>
        </Modal>
    );
};

// Modals have to be exported as Default for lazy loading
export default HelpGuideModal;
